/* global BigInt */
/**
 * useCartUsdcPayment — orchestration du paiement USDC on-chain (Avalanche) pour le panier.
 *
 * Phase 1, TRACK-2026-06-24-usdc-button-multichain.
 * §74 : aucun hook équivalent (grep useWriteContract/useReadContract/usdc/prepare = 0).
 * §9 DRY : responsabilité unique = orchestrer le flux on-chain. Le composant ne gère que l'UI.
 * §46 : l'asset est livré par `confirm` côté backend APRÈS le receipt on-chain (jamais ici).
 * §26 : aucun état/secret persisté ; la clé reste dans le wallet de l'user (signature wagmi).
 *
 * Flux (driven par `pay()`):
 *   1. POST /cart/usdc/prepare → {contractAddress, usdcAddress, sellers, amounts, totalUsdc, cartOrderId}
 *   2. lire allowance ERC20 (skip approve si suffisante) ; sinon approve(contractAddress, totalUsdc)
 *   3. batchBuyItems(cartOrderId, sellers, amounts) sur contractAddress + attendre le receipt
 *   4. POST /cart/usdc/confirm {cartOrderId, txHash} → {status, completed, failed}
 *
 * Renvoie une machine d'état simple consommée par CartCheckoutModal (phase + erreur + résultat).
 */
import { useState, useCallback } from 'react';
import {
  useAccount,
  useChainId,
  useSwitchChain,
  useConfig,
} from 'wagmi';
import {
  readContract,
  writeContract,
  waitForTransactionReceipt,
} from 'wagmi/actions';
import { axiosWithToken } from '../utils/functions/apiCallWithToken';
import { resolvedChainId } from '../components/EarniWalletModal/config/wagmiConfig';
import { buildCartItemsPayload } from '../utils/cart/cartConstants';
import { SERVER_URL as serverURL } from '../core/config';


/* ─── Avalanche chain id (hex ou décimal supporté par resolvedChainId) ───────── */
export const AVALANCHE_CHAIN_ID = resolvedChainId;

/* ─── ABI minimal ────────────────────────────────────────────────────────────
 * batchBuyItems : copié de cartUsdcService BATCH_BUY_ABI (signature identique).
 * ERC20 approve/allowance : standard, pour autoriser le contrat à débiter l'USDC.
 */
export const BATCH_BUY_ABI = [
  {
    name: 'batchBuyItems',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'cartOrderId', type: 'uint256' },
      { name: 'sellers', type: 'address[]' },
      { name: 'amounts', type: 'uint256[]' },
    ],
    outputs: [],
  },
];

export const ERC20_ABI = [
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
];

/* ─── Phases de progression (pour l'UI) ──────────────────────────────────────── */
export const USDC_PHASE = {
  IDLE: 'IDLE',
  PREPARING: 'PREPARING',   // POST /prepare
  APPROVING: 'APPROVING',   // approve ERC20 (signature + receipt)
  PURCHASING: 'PURCHASING', // batchBuyItems (signature + receipt)
  CONFIRMING: 'CONFIRMING', // POST /confirm
  DONE: 'DONE',
  ERROR: 'ERROR',
};

/**
 * @param {{ onResult: (res:{status,completed,failed}) => void }} opts
 * @returns {{
 *   pay: (cartItems:Array) => Promise<void>,
 *   phase: string,
 *   error: string|null,
 *   reset: () => void,
 * }}
 */
export function useCartUsdcPayment({ onResult, onInvalidItems } = {}) {
  const config = useConfig();
  const { address } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const [phase, setPhase] = useState(USDC_PHASE.IDLE);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  const reset = useCallback(() => {
    setPhase(USDC_PHASE.IDLE);
    setError(null);
    setErrorCode(null);
  }, []);

  const pay = useCallback(async (cartItems) => {
    setError(null);
    setErrorCode(null);
    try {
      // 0. S'assurer que le wallet est sur Avalanche (sinon switch).
      // useChainId est lu au moment de l'appel via config.state pour rester à jour.
      if (Number.isFinite(AVALANCHE_CHAIN_ID)) {
        try {
          await switchChainAsync({ chainId: AVALANCHE_CHAIN_ID });
        } catch (switchErr) {
          // Si le switch échoue (refus user / wallet ne supporte pas), on remonte une erreur claire.
          throw Object.assign(new Error('SWITCH_CHAIN_FAILED'), { _code: 'SWITCH_CHAIN_FAILED', _userMessage: 'Please switch your wallet to the Avalanche network and try again.' });
        }
      }

      // 1. Prepare côté backend (source de vérité des montants/sellers).
      setPhase(USDC_PHASE.PREPARING);
      const items = buildCartItemsPayload(cartItems);
      const prep = await axiosWithToken(`${serverURL}/cart/usdc/prepare`, 'post', { items });
      const { cartOrderId, contractAddress, usdcAddress, sellers, amounts, totalUsdc } = prep || {};
      if (!cartOrderId || !contractAddress || !usdcAddress || !Array.isArray(sellers) || !Array.isArray(amounts)) {
        throw Object.assign(new Error('PREPARE_INVALID'), { _code: 'PREPARE_INVALID', _userMessage: 'Unable to prepare the USDC payment. Please try again.' });
      }

      const total = BigInt(totalUsdc);

      // 2. Allowance : lire l'allowance actuelle, approve uniquement si insuffisante (anti-RPC race :
      //    on relit après approve pour confirmer plutôt que de supposer).
      const currentAllowance = await readContract(config, {
        address: usdcAddress,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [address, contractAddress],
      });

      if (BigInt(currentAllowance) < total) {
        setPhase(USDC_PHASE.APPROVING);
        const approveHash = await writeContract(config, {
          address: usdcAddress,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [contractAddress, total],
        });
        await waitForTransactionReceipt(config, { hash: approveHash });
      }

      // 3. batchBuyItems — le contrat tire l'USDC PUIS distribue (§46 géré on-chain).
      setPhase(USDC_PHASE.PURCHASING);
      const sellerArgs = sellers.map((s) => s);
      const amountArgs = amounts.map((a) => BigInt(a));
      const txHash = await writeContract(config, {
        address: contractAddress,
        abi: BATCH_BUY_ABI,
        functionName: 'batchBuyItems',
        args: [BigInt(cartOrderId), sellerArgs, amountArgs],
      });
      const receipt = await waitForTransactionReceipt(config, { hash: txHash });
      if (receipt?.status !== 'success') {
        throw Object.assign(new Error('TX_REVERTED'), { _code: 'TX_REVERTED', _userMessage: 'The transaction failed on-chain. No items were purchased.' });
      }

      // 4. Confirm côté backend → vérifie l'event BatchPurchased, livre l'asset, dispatch vendeur.
      setPhase(USDC_PHASE.CONFIRMING);
      const res = await axiosWithToken(`${serverURL}/cart/usdc/confirm`, 'post', { cartOrderId, txHash });

      setPhase(USDC_PHASE.DONE);
      if (typeof onResult === 'function') {
        onResult({
          status: res?.status || (res?.success ? 'COMPLETED' : 'FAILED'),
          completed: res?.completed,
          failed: res?.failed,
        });
      }
    } catch (err) {
      // Gate 2 (CART_ITEMS_INVALID) — un item est devenu indisponible entre REVIEW et /prepare.
      // Le backend renvoie 422 {invalid, valid} AVANT toute opération on-chain : RIEN n'est débité.
      // On ne bascule PAS en phase ERROR (ce n'est pas un échec on-chain) ; on délègue au composant
      // qui ré-applique l'UX Gate 1 (retrait + toast + retour REVIEW). axiosWithToken décore l'Error
      // avec {code, invalid, valid} (cf. apiCallWithToken else-branch).
      if (
        (err?.code === 'CART_ITEMS_INVALID' || Array.isArray(err?.invalid)) &&
        typeof onInvalidItems === 'function'
      ) {
        reset();
        onInvalidItems(err.invalid || []);
        return;
      }
      // Rejet de signature par l'user (wagmi/viem) → message dédié, retour possible.
      const isUserReject =
        err?.name === 'UserRejectedRequestError' ||
        /user rejected|user denied|rejected the request/i.test(err?.message || err?.shortMessage || '');
      const code = isUserReject ? 'USER_REJECTED' : (err?._code || 'GENERIC');
      const msg = isUserReject
        ? 'You declined the transaction. No payment was made.'
        : (err?._userMessage ||
           err?.response?.data?.message ||
           err?.shortMessage ||
           err?.message ||
           'USDC payment failed. Please try again.');
      setErrorCode(code);
      setError(msg);
      setPhase(USDC_PHASE.ERROR);
    }
  }, [config, address, switchChainAsync, onResult, onInvalidItems, reset]);

  return { pay, phase, error, errorCode, reset };
}

export default useCartUsdcPayment;
